import { useTranslation } from "gatsby-plugin-react-i18next";
import { useState, type FC } from "react";
import { Controller, useForm } from "react-hook-form";
import AddToCartButton, {
	State,
} from "../../components/AddToCartButton/AddToCartButton";
import FormInformation from "../../components/Checkout/components/FormInformation/FormInformation";
import Form from "../../components/general/Form";
import Input from "../../components/general/Input";
import Link from "../../components/general/Link";
import PillInput from "../../components/general/Pills";
import PillGroup from "../../components/general/Pills/PillGroup";
import * as s from "./GiftCard.module.scss";

export type GiftCardFormValues = {
	recipient: string;
	email: string;
	sender?: string;
	message?: string;
};

type AmountProps = {
	id: string;
	amount: string;
	url?: string;
	active?: boolean;
};

type GiftCardProps = {
	productName: string;
	amounts: AmountProps[];
	submit: (
		values: GiftCardFormValues,
	) => Promise<{ errorMessage?: string } | undefined>;
};

const GiftCard: FC<GiftCardProps> = ({ productName, amounts, submit }) => {
	const { t } = useTranslation();
	const [btnState, setBtnState] = useState<State>(State.Idle);
	const [errorMessage, setErrorMessage] = useState<undefined | string>();
	const [chosenAmountIndex, setChosenAmountIndex] = useState(
		amounts.findIndex((amount) => amount.active),
	);

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<GiftCardFormValues>({
		mode: "onTouched",
		defaultValues: {
			recipient: "",
			email: "",
			sender: "",
			message: "",
		},
	});

	const hasFieldError = (
		key: keyof GiftCardFormValues,
	): boolean | undefined => {
		return errors[key] ? true : undefined;
	};

	const onSubmit = async (values: GiftCardFormValues) => {
		setErrorMessage(undefined);
		setBtnState(State.Pending);
		try {
			const response = await submit(values);

			if (response?.errorMessage) {
				setErrorMessage(response.errorMessage);
				setBtnState(State.Idle);
			} else {
				setBtnState(State.Added);
				reset();
			}
		} catch {
			setErrorMessage("Unknown error");
			setBtnState(State.Idle);
		}
	};

	return (
		<section className={s.wrapper}>
			<header className={s.header}>
				<h2>{productName}</h2>
			</header>
			<PillGroup label={t("Sum")} className={s.sumContainer} priceModule>
				{amounts.map((item, index) => {
					const Pill = () => (
						<PillInput
							id={item.id}
							label={item.amount}
							name={item.amount}
							value={item.amount}
							onChange={() => setChosenAmountIndex(index)}
							checked={chosenAmountIndex === index}
						/>
					);

					if (item.url) {
						return (
							<Link key={item.id} href={item.url}>
								<Pill />
							</Link>
						);
					}
					return <Pill key={item.id} />;
				})}
			</PillGroup>
			<Form
				onSubmit={() => {
					void handleSubmit(onSubmit)();
				}}
				className={s.form}
				autoComplete="off"
				formInformation={
					errorMessage
						? {
								icon: "",
								label: t("Error"),
								information: errorMessage,
							}
						: undefined
				}
			>
				<Controller
					name="recipient"
					control={control}
					render={({ field }) => (
						<Input
							label={t("Recipient *")}
							placeholder={t("Name")}
							errorMessage={t("Invalid name")}
							type="text"
							error={hasFieldError("recipient")}
							{...field}
						/>
					)}
					rules={{
						required: true,
					}}
				/>

				<Controller
					name="email"
					control={control}
					render={({ field }) => (
						<Input
							label={t("E-mail for delivery *")}
							placeholder={t("Email")}
							errorMessage={t("Invalid email")}
							type="email"
							error={hasFieldError("email")}
							{...field}
						/>
					)}
					rules={{
						required: true,
						pattern: /^[\w%+.-]+@[\d.a-z-]+\.[a-z]{2,}$/i,
					}}
				/>

				<Controller
					name="sender"
					control={control}
					render={({ field }) => (
						<Input
							label={t("From")}
							placeholder={t("Name")}
							type="text"
							{...field}
						/>
					)}
					rules={{
						required: false,
					}}
				/>

				<Controller
					name="message"
					control={control}
					render={({ field }) => (
						<Input
							label={t("Message (max 250)")}
							errorMessage={t("Invalid message")}
							type="text"
							error={hasFieldError("message")}
							{...field}
						/>
					)}
					rules={{
						maxLength: 250,
					}}
				/>

				<div className={s.priceContainer}>
					<p>{amounts[chosenAmountIndex]?.amount}</p>
				</div>

				{errorMessage && errorMessage.length > 0 && (
					<FormInformation icon="" label="" information={errorMessage} />
				)}

				<AddToCartButton
					fullWidth
					state={btnState}
					label={t("Add to cart")}
					type="submit"
				/>
			</Form>
		</section>
	);
};

export default GiftCard;
